// Remove a work item created in error — admin-only (work:remove), recoverable.
//
// Removal takes the item (and its live sub-items) out of every working and
// reporting view; nothing is deleted and Restore brings it back. A reason CODE
// is required ('other' also needs a note). When the item carries history that
// matters — it is completed, it has sub-items, or effort was recorded — the
// dialog spells that out and asks for an explicit acknowledgement before the
// button arms (the owner's "stronger confirm", D3). Recorded effort is NOT
// pulled back out of payroll; the dialog says so rather than implying it.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/features/shared/components/ui/dialog';
import { Button } from '@/features/shared/components/ui/button';
import { Checkbox } from '@/features/shared/components/ui/checkbox';
import { Input } from '@/features/shared/components/ui/input';
import { Label } from '@/features/shared/components/ui/label';
import { getApiErrorMessage } from '@/lib/apiError';
import {
  WORK_REMOVAL_REASONS, type WorkItem, type WorkRemovalReason, type WorkRemovalResult, removeWorkItem, restoreWorkItem,
} from '@/services/workService';

type Translate = (key: string, opts?: Record<string, unknown>) => unknown;

/** Restore an item (its whole removal batch) and say what happened — including
 *  anything restore decided on its own (an owner who has left, a stopped
 *  series). Refusals ("restore the parent first") are shown verbatim. */
export async function restoreWithFeedback(id: string, t: Translate, onError?: (message: string) => void): Promise<WorkRemovalResult | null> {
  try {
    const r = await restoreWorkItem(id);
    const head = (r.count > 1
      ? t('restored_ok_many', { defaultValue: 'Restored {{count}} items.', count: r.count })
      : t('restored_ok', { defaultValue: 'Restored.' })) as string;
    toast.success([head, ...(r.notes || [])].join(' '), { duration: r.notes?.length ? 8000 : 4000 });
    return r;
  } catch (e) {
    const message = getApiErrorMessage(e, t('restore_failed', { defaultValue: 'Could not restore this work item.' }) as string);
    toast.error(message);
    onError?.(message); // the caller may also show it in place (the item page's banner)
    return null;
  }
}

/** What makes a removal weighty enough to need the explicit acknowledgement. */
export function removalWarnings(item: Pick<WorkItem, 'status' | 'effort_hours'>, subitems: number): {
  completed: boolean; subitems: number; effortHours: number; strong: boolean;
} {
  const completed = item.status === 'completed';
  const effortHours = item.effort_hours && item.effort_hours > 0 ? item.effort_hours : 0;
  return { completed, subitems, effortHours, strong: completed || subitems > 0 || effortHours > 0 };
}

export const RemoveWorkDialog: React.FC<{
  open: boolean;
  item: WorkItem;
  /** Sub-items that would be removed with it (detail.subitems.total). */
  subitems?: number;
  onClose: () => void;
  onDone: (r: WorkRemovalResult) => void;
}> = ({ open, item, subitems = 0, onClose, onDone }) => {
  const { t } = useTranslation('work');
  const [reason, setReason] = useState<WorkRemovalReason | ''>('');
  const [note, setNote] = useState('');
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  // A fresh decision every time — never a stale reason submitted unread.
  useEffect(() => { if (open) { setReason(''); setNote(''); setAck(false); setBusy(false); } }, [open, item.id]);

  const w = removalWarnings(item, subitems);
  const needsNote = reason === 'other' && !note.trim();
  const blocked = busy || !reason || needsNote || (w.strong && !ack);
  const close = () => { if (!busy) onClose(); };

  const submit = async () => {
    if (blocked || !reason) return;
    setBusy(true);
    try {
      const r = await removeWorkItem(item.id, { reason_code: reason, note: note.trim() || undefined, expected_updated_at: item.updated_at });
      toast.success((r.count > 1
        ? t('removed_ok_many', { defaultValue: 'Removed {{count}} items. Restore them from the Removed filter.', count: r.count })
        : t('removed_ok', { defaultValue: 'Removed. Restore it from the Removed filter.' })) as string);
      onDone(r);
      onClose();
    } catch (e) {
      toast.error(getApiErrorMessage(e, t('remove_failed', { defaultValue: 'Could not remove this work item.' })));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('remove_title', { defaultValue: 'Remove “{{title}}”?', title: item.title })}</DialogTitle>
          <DialogDescription>
            {t('remove_help', { defaultValue: 'For work created in error. It leaves every list, dashboard, calendar and report — but nothing is deleted, and an administrator can restore it. To stop real work, cancel it instead.' })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {w.strong && (
            <div className="rounded-md border border-border bg-warning-soft p-3 text-sm text-foreground space-y-1" data-testid="remove-strong-warning">
              {w.completed && <p>{t('remove_warn_completed', { defaultValue: 'This work is completed. Its status stays as it is — it simply stops counting anywhere.' })}</p>}
              {w.subitems > 0 && <p>{t('remove_warn_subitems', { defaultValue: 'Its {{count}} sub-items are removed with it (and restored with it).', count: w.subitems })}</p>}
              {w.effortHours > 0 && <p>{t('remove_warn_effort', { defaultValue: '{{hours}} h of effort was recorded on it. Hours already sent to payroll are NOT taken back — check the payroll run if that matters.', hours: w.effortHours })}</p>}
              <label className="flex items-start gap-2 pt-1" htmlFor={`remove-ack-${item.id}`}>
                <Checkbox id={`remove-ack-${item.id}`} checked={ack} disabled={busy} onCheckedChange={(v) => setAck(v === true)} className="mt-0.5" />
                <span>{t('remove_ack', { defaultValue: 'I understand — remove it anyway' })}</span>
              </label>
            </div>
          )}
          <div>
            <Label htmlFor={`remove-reason-${item.id}`} className="text-xs">{t('remove_reason', { defaultValue: 'Why is it being removed?' })} *</Label>
            <select id={`remove-reason-${item.id}`} className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              value={reason} disabled={busy} onChange={(e) => setReason(e.target.value as WorkRemovalReason | '')}>
              <option value="">{t('remove_choose', { defaultValue: 'Choose a reason…' })}</option>
              {WORK_REMOVAL_REASONS.map((r) => (
                <option key={r.code} value={r.code}>{t(`remove_reason_${r.code}`, { defaultValue: r.label })}</option>
              ))}
            </select>
          </div>
          <div>
            <Label htmlFor={`remove-note-${item.id}`} className="text-xs">
              {reason === 'other' ? `${t('remove_note', { defaultValue: 'Note' })} *` : t('remove_note_optional', { defaultValue: 'Note (optional)' })}
            </Label>
            <Input id={`remove-note-${item.id}`} value={note} disabled={busy} onChange={(e) => setNote(e.target.value)}
              placeholder={t('remove_note_ph', { defaultValue: 'e.g. duplicate of WI-214' }) as string} />
            {needsNote && <p className="mt-1 text-xs text-danger-strong">{t('remove_note_required', { defaultValue: '“Other” needs a short note saying why.' })}</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={close} disabled={busy}>{t('keep', { defaultValue: 'Keep it' })}</Button>
          <Button variant="destructive" onClick={() => void submit()} disabled={blocked}>
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
            {t('remove', { defaultValue: 'Remove' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RemoveWorkDialog;
